import type { PageDataMap, SlotGroupOverridesMap } from '@vibesites/runtime';
import parentPagesData from '../blue-haven-pages.json';
import localPagesData from '../pages.json';

// ============================================================================
// Shared navigation eligibility
// ----------------------------------------------------------------------------
// Single source of truth for "which pages appear in the primary navigation".
// Consumed by Header (renders the nav) and SitemapContent (mirrors nav plus
// CTA-only pages like Schedule a Tour; omits outbound Apply / Resident
// links). See buildNavLinks / buildSitemapLinks / getEligibleNavPages below.
//
// Parent theme ships the full page catalog; this company theme only stores
// overrides in pages.json. Merge them here so nav respects local
// visible/noindex changes (e.g. Apply Now + Resident Access hidden in favor
// of external OLL / resident-portal CTAs).
// ============================================================================

/** Slot-group path for the Property Map page image (SiteMapImage › image). */
export const SITE_MAP_IMAGE_GROUP_PATH = 'sitemapimage.image';

/**
 * True when any scope has an uploaded Property Map image. Overrides for
 * SiteMapImage are saved on the `/property-map` page, so callers must scan
 * the full override map (not only the current page's scoped resolve) or the
 * header/sitemap on other pages would miss the upload.
 */
export function hasSiteMapImageOverride(
  overrides: SlotGroupOverridesMap | undefined | null,
): boolean {
  if (!overrides) return false;
  for (const [key, override] of Object.entries(overrides)) {
    const path = key.includes('::') ? key.slice(key.lastIndexOf('::') + 2) : key;
    if (path !== SITE_MAP_IMAGE_GROUP_PATH) continue;
    const field = override?.fieldOverrides?.image;
    const value = field?.customValue ?? field?.generatedValue;
    if (value && String(value).trim()) return true;
  }
  return false;
}

export interface PageEntry {
  name: string;
  path: string;
  position: number;
  visible?: boolean;
  template?: string;
  noindex?: boolean;
  /** When set, this page is rendered as a sub-nav item under the parent
      page's nav entry (a dropdown on desktop, indented row on mobile). */
  parent?: string;
}

export interface NavLink {
  label: string;
  href: string;
  /** Open in a new tab for off-site destinations (OLL, resident portal). */
  target?: string;
  /** Optional catalog event for off-site CTAs (see tracking-events). */
  event?: 'apply_now_clicked' | 'resident_portal_clicked';
  /** Sub-nav items rendered as a dropdown on desktop and indented rows on
      mobile. Populated when other pages declare `parent: <this alias>`. */
  children?: NavLink[];
}

/**
 * Nav items that must render as children of another alias (desktop hover
 * dropdown, mobile indented row) — never as their own top-level link.
 *
 * `pages.json` `parent` is not an official PageDataJSON field, so the
 * platform merge can drop it and leave Pet Policy sitting out in the bar.
 * This map is the source of truth; JSON `parent` still wins when present.
 */
const NAV_NEST_UNDER: Record<string, string> = {
  'pet-policy': 'amenities',
  'interactive-map': 'floor-plans',
  'site-map': 'floor-plans',
};

/** Page aliases that should never appear in the main navigation.
    Apply Now and Resident Login are excluded here — Haven Realty Capital
    surfaces them as external OLL / resident-portal CTAs instead of in-site
    pages. Schedule a Tour is excluded from the link row because the primary
    header CTA routes to Contact Us (already in the nav). Pet Policy
    nests under Amenities via NAV_NEST_UNDER (same pattern as
    Interactive Map / Property Map under Floor Plans). Reviews is not
    excluded — it follows pages.json `visible` (parent defaults false;
    enable in nav by setting visible: true). */
export const NAV_EXCLUDED_ALIASES = new Set<string>([
  'home',
  'privacy-policy',
  'sitemap',
  'accessibility',
  'thank-you',
  'schedule-a-tour',
  'hyperlocal',
  'apply-now',
  'resident-access',
]);

/** Aliases kept out of the header link row but still listed on the sitemap
    when visible. Empty now that Schedule a Tour CTAs route to Contact Us
    (already on the sitemap via the Contact Us nav page). */
export const SITEMAP_EXTRA_ALIASES = new Set<string>([]);

/** Shallow-merge parent nav metadata with this theme's pages.json overrides. */
function mergedNavPages(): Record<string, PageEntry> {
  const parent = parentPagesData as PageDataMap;
  const local = localPagesData as PageDataMap;
  const aliases = new Set([...Object.keys(parent), ...Object.keys(local)]);
  const result: Record<string, PageEntry> = {};

  for (const alias of aliases) {
    const base = parent[alias] || {};
    const override = local[alias] || {};
    const merged = { ...base, ...override };
    if (merged.deleted_at) continue;
    result[alias] = {
      name: merged.name ?? alias,
      path: merged.path ?? `/${alias}`,
      position: merged.position ?? 0,
      visible: merged.visible,
      template: merged.template,
      noindex: merged.noindex,
      parent: (merged as { parent?: string }).parent || NAV_NEST_UNDER[alias],
    };
  }

  return result;
}

const pagesData = mergedNavPages();

/** Normalize a pathname for active-link comparison: drop the query/hash and
    trailing slashes so `/floor-plans`, `/floor-plans/`, and
    `/floor-plans?x=1` all match the `/floor-plans` nav href. Root stays `/`. */
export function normalizePath(p: string): string {
  const path = (p || '').split(/[?#]/)[0];
  return path.replace(/\/+$/, '') || '/';
}

/**
 * Flat, position-sorted list of `[alias, page]` entries eligible for the
 * primary navigation. This is the canonical "pages in navigation" filter:
 * excluded aliases, hidden pages, and template pages are dropped, and the
 * Virtual Tour / Interactive Map / Property Map pages appear only when the
 * property has the data to back them (see the flags passed from the caller).
 */
function isPageListable(
  alias: string,
  page: PageEntry,
  hasVirtualTours: boolean,
  hasInteractiveLeasing: boolean,
  hasSiteMapImage: boolean,
  allowExtra: boolean,
): boolean {
  if (allowExtra && SITEMAP_EXTRA_ALIASES.has(alias)) {
    if (page.visible === false) return false;
    if (page.template) return false;
    return true;
  }
  if (NAV_EXCLUDED_ALIASES.has(alias)) return false;
  if (page.visible === false) return false;
  if (page.template) return false;
  if (alias === 'virtual-tour' && !hasVirtualTours) return false;
  if (alias === 'interactive-map' && !hasInteractiveLeasing) return false;
  if (alias === 'site-map' && !hasSiteMapImage) return false;
  return true;
}

export function getEligibleNavPages(
  hasVirtualTours: boolean,
  hasInteractiveLeasing: boolean,
  hasSiteMapImage: boolean,
): Array<[string, PageEntry]> {
  return (Object.entries(pagesData) as Array<[string, PageEntry]>)
    .filter(([alias, page]) =>
      isPageListable(
        alias,
        page,
        hasVirtualTours,
        hasInteractiveLeasing,
        hasSiteMapImage,
        false,
      ),
    )
    .sort(([, a], [, b]) => a.position - b.position);
}

/** Nav-eligible pages plus sitemap extras (e.g. Schedule a Tour). Apply Now /
    Resident Access stay excluded — those only link out from the header. */
export function getEligibleSitemapPages(
  hasVirtualTours: boolean,
  hasInteractiveLeasing: boolean,
  hasSiteMapImage: boolean,
): Array<[string, PageEntry]> {
  return (Object.entries(pagesData) as Array<[string, PageEntry]>)
    .filter(([alias, page]) =>
      isPageListable(
        alias,
        page,
        hasVirtualTours,
        hasInteractiveLeasing,
        hasSiteMapImage,
        true,
      ),
    )
    .sort(([, a], [, b]) => a.position - b.position);
}

export interface ExternalNavCtas {
  /** Online leasing (OLL) / apply URL — opens in a new tab. */
  applyNowUrl?: string | null;
  /** Resident portal URL — opens in a new tab. */
  residentPortalUrl?: string | null;
  applyNowLabel?: string;
  residentPortalLabel?: string;
}

/**
 * Build a parent/child NavLink tree from an eligible `[alias, page]` list,
 * then optionally append external CTAs after Contact Us.
 */
function buildLinksFromEligible(
  eligible: Array<[string, PageEntry]>,
  externalCtas?: ExternalNavCtas,
): NavLink[] {
  const eligibleAliases = new Set(eligible.map(([alias]) => alias));
  const linksByAlias = new Map<string, NavLink>();
  const topLevel: NavLink[] = [];

  for (const [alias, page] of eligible) {
    linksByAlias.set(alias, { label: page.name, href: page.path });
  }

  for (const [alias, page] of eligible) {
    const link = linksByAlias.get(alias)!;
    const nestUnder = page.parent || NAV_NEST_UNDER[alias];
    if (nestUnder && nestUnder !== alias) {
      if (eligibleAliases.has(nestUnder)) {
        const parentLink = linksByAlias.get(nestUnder)!;
        (parentLink.children ??= []).push(link);
      }
      // Parent hidden / missing: drop rather than promoting a nested page
      // (e.g. Pet Policy) into the top-level bar.
    } else {
      topLevel.push(link);
    }
  }

  const extras: NavLink[] = [];
  const applyUrl = externalCtas?.applyNowUrl?.trim();
  if (applyUrl && applyUrl !== '#') {
    extras.push({
      label: externalCtas?.applyNowLabel || 'Apply Now',
      href: applyUrl,
      target: '_blank',
      event: 'apply_now_clicked',
    });
  }
  const portalUrl = externalCtas?.residentPortalUrl?.trim();
  if (portalUrl && portalUrl !== '#') {
    extras.push({
      label: externalCtas?.residentPortalLabel || 'Resident Login',
      href: portalUrl,
      target: '_blank',
      event: 'resident_portal_clicked',
    });
  }

  if (extras.length === 0) return topLevel;

  const contactIdx = topLevel.findIndex(
    (l) => normalizePath(l.href) === '/contact-us' || l.label.toLowerCase() === 'contact us',
  );
  if (contactIdx >= 0) {
    topLevel.splice(contactIdx + 1, 0, ...extras);
    return topLevel;
  }
  return [...topLevel, ...extras];
}

/**
 * Build navigation links from the merged page catalog. Only includes eligible
 * pages (see getEligibleNavPages), sorted by position. Pages declaring
 * `parent: <alias>` (or NAV_NEST_UNDER) are nested as `children` of their
 * parent entry rather than appearing at the top level. Nested pages whose
 * parent is missing or hidden are dropped so they cannot sit out in the bar.
 *
 * When `externalCtas` is provided, Apply Now (OLL) and Resident Login are
 * appended after Contact Us (or at the end) as off-site / extra links —
 * replacing the in-theme /apply-now and /resident-access pages.
 */
export function buildNavLinks(
  hasVirtualTours: boolean,
  hasInteractiveLeasing: boolean,
  hasSiteMapImage: boolean,
  externalCtas?: ExternalNavCtas,
): NavLink[] {
  return buildLinksFromEligible(
    getEligibleNavPages(hasVirtualTours, hasInteractiveLeasing, hasSiteMapImage),
    externalCtas,
  );
}

/**
 * Sitemap link list: same eligibility as the header link row. Does not
 * surface outbound-only links (Apply Now, Resident Portal).
 */
export function buildSitemapLinks(
  hasVirtualTours: boolean,
  hasInteractiveLeasing: boolean,
  hasSiteMapImage: boolean,
): NavLink[] {
  return buildLinksFromEligible(
    getEligibleSitemapPages(hasVirtualTours, hasInteractiveLeasing, hasSiteMapImage),
  );
}
